/**
 * <MoneyAmount /> — unified currency display.
 *
 * Renders an amount in the user's profile currency, prefixing `≈` when the
 * source record was originally in another currency. Optionally renders a
 * secondary FX line ("≈ 1000 USD @ 3648.4479") below.
 *
 * Replaces the noisy "Exact"/"Approx." chips we had before — the `≈` symbol
 * alone is the agreed-upon FX indicator across the app.
 *
 * Props:
 *   amount             : number  - converted (profile-currency) value to show
 *   currency           : string  - profile currency (USD, COP, …)
 *   original_currency  : string? - source currency, if FX
 *   original_amount    : number? - amount in source currency
 *   fx_rate            : number? - rate (per-original-unit) at insert time
 *   fx_at              : string? - ISO timestamp when the rate was captured
 *                                  (drives the audit tooltip "Tasa registrada
 *                                  el {fecha}..."). Optional — older rows that
 *                                  don't have it just fall back to the generic
 *                                  "tasa puede haber cambiado" copy.
 *   sign               : "+" | "-" | null — optional prefix for income/expense
 *   tone               : "income" | "expense" | "default" — color
 *   size               : "sm" | "md" | "lg" — visual scale (default md)
 *   showFxLine         : boolean — render the second line. Default true.
 *   testid             : string  — base data-testid
 */
import { formatMoney } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";

const SIZE_CLASSES = {
  sm: "text-sm",
  md: "text-base sm:text-lg",
  lg: "font-display text-2xl sm:text-3xl",
};

const TONE_CLASSES = {
  income: "text-emerald-700",
  expense: "text-red-600",
  default: "text-slate-900",
};

export function MoneyAmount({
  amount,
  currency,
  original_currency,
  original_amount,
  fx_rate,
  fx_at,
  sign = null,
  tone = "default",
  size = "md",
  showFxLine = true,
  testid = "money-amount",
  className = "",
}) {
  const { lang } = useLang();
  const isFx = original_currency && original_currency !== currency
    && original_amount != null;

  const sizeCls = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.default;

  // Build the FX audit tooltip — same text on both the ≈ symbol and
  // the sub-line for consistency. When fx_at is present we emphasise
  // WHEN the rate was captured (the user just asked us to make that
  // auditable). Format keeps the locale of the active language.
  const fxFormatted = isFx && fx_at
    ? new Date(fx_at).toLocaleDateString(tx(lang, "en", "es", "es"), {
        day: "2-digit", month: "long", year: "numeric",
      })
    : null;
  const fxTooltip = !isFx
    ? null
    : fxFormatted
      ? (tx(lang, `Rate captured on ${fxFormatted}: 1 ${original_currency} = ${fx_rate ? Number(fx_rate).toLocaleString("en", { maximumFractionDigits: 4 }) : "?"} ${currency}. Kept fixed even if the market rate has shifted since.`, `Tasa registrada el ${fxFormatted}: 1 ${original_currency} = ${fx_rate ? Number(fx_rate).toLocaleString("es", { maximumFractionDigits: 4 }) : "?"} ${currency}. Se mantiene aunque la tasa de mercado haya cambiado desde entonces.`, `Tasa registrada el ${fxFormatted}: 1 ${original_currency} = ${fx_rate ? Number(fx_rate).toLocaleString("es", { maximumFractionDigits: 4 }) : "?"} ${currency}. Se mantiene aunque la tasa de mercado haya cambiado desde entonces.`))
      : (tx(lang, `Approximate value in ${currency}. FX rate may have shifted.`, `Valor aproximado en ${currency}. La tasa FX puede haber cambiado.`, `Valor aproximado en ${currency}. La tasa FX puede haber cambiado.`));

  return (
    <div
      data-testid={testid}
      className={`flex flex-col items-end ${className}`}
    >
      <div
        className={`font-bold tabular-nums flex items-center gap-1 ${sizeCls} ${toneCls}`}
        data-testid={`${testid}-value`}
      >
        {sign && <span>{sign}</span>}
        {isFx && (
          <span
            data-testid={`${testid}-fx-symbol`}
            className="text-slate-400 font-medium cursor-help"
            title={fxTooltip}
          >
            ≈
          </span>
        )}
        {formatMoney(amount, currency)}
      </div>
      {isFx && showFxLine && (
        <FxLine
          original_amount={original_amount}
          original_currency={original_currency}
          fx_rate={fx_rate}
          tooltip={fxTooltip}
          testid={`${testid}-fx-line`}
        />
      )}
    </div>
  );
}

function FxLine({ original_amount, original_currency, fx_rate, tooltip, testid }) {
  const { lang } = useLang();
  const formatted = Number(original_amount).toLocaleString(
    tx(lang, "en", "es", "es"),
    { maximumFractionDigits: 2 },
  );
  const rate = fx_rate
    ? Number(fx_rate).toLocaleString(tx(lang, "en", "es", "es"), {
        maximumFractionDigits: 4,
      })
    : null;
  return (
    <span
      data-testid={testid}
      className="text-[10px] font-medium text-slate-500 tabular-nums whitespace-nowrap cursor-help"
      title={tooltip || (tx(lang, `Originally recorded in ${original_currency}. The rate may have shifted since.`, `Registrado originalmente en ${original_currency}. La tasa puede haber cambiado.`, `Registrado originalmente en ${original_currency}. La tasa puede haber cambiado.`))}
    >
      ≈ {formatted} {original_currency}
      {rate && <span className="text-slate-400"> @ {rate}</span>}
    </span>
  );
}
